import React, { useCallback, useState } from 'react';
import { Route, BrowserRouter as Router, Switch } from 'react-router-dom';
import Clarity from '@microsoft/clarity';
import { DateRangeProvider } from './state/DateRangeContext';
import Auth from './screens/Auth';
import Builder from './pages/NewBuilder';
import Site from './pages/Site';
import {
  isLoggedIn,
  confirmTermsOfService,
  getShop,
  getUser,
  isInternalLoginUser,
} from './framework/util/auth';
import PageListener from './util/PageListener';
import { TemplateProvider } from './state/TemplateContext';
import { BaseComponentProvider } from './state/BaseComponentContext';
import { getIntercomSettings, attachIntercomScript } from './widgets/intercom';
import { setServerUriToSession } from './framework/util/rest';
import ErrorModal from './widgets/builder/modals/ErrorModal';
import TermsOfServiceModal from './framework/modals/TermsOfServiceModal';
import { attachGoogleAnalyticsScript } from './widgets/googleAnalytics';
import updateDocumentTitle from './util/DocumentUtil';
import ErrorBoundary from './util/ErrorBoundary';
import Config from './Config';

const appName = 'spently';
export default function App() {
  const [errorState, setIsErrorState] = useState({
    isError: false,
    exception: null,
  });
  const path = document.location.pathname
    ? document.location.pathname.toLowerCase()
    : 'Home';

  Clarity.init(Config.spently.clarityCode);
  // on user accept the terms of service
  const onAccept = useCallback(() => {
    confirmTermsOfService(appName);
  }, []);

  try {
    if (!isLoggedIn(appName) && path !== '/authorize') {
      return <Auth />;
    }
  } catch (e) {
    setIsErrorState({ isError: true, e });
    return <ErrorModal />;
  }

  const user = getUser('spently');
  const shop = getShop(appName);
  Clarity.identify(
    shop,
    null,
    null,
    `${shop}: ${user?.first_name} ${user?.last_name}`
  );
  updateDocumentTitle();
  window.intercomSettings = getIntercomSettings();
  attachIntercomScript();
  attachGoogleAnalyticsScript();
  setServerUriToSession();

  // if session storage has onboarding flag and the current page isn't /Builder, redirect to /Builder
  if (
    sessionStorage.getItem(`${appName}-onboarding`) === 'true' &&
    !path.toLowerCase().startsWith('/builder')
  ) {
    sessionStorage.setItem(`${appName}-onboarding`, false);
    window.location = '/Builder?template=OI0030&set=true';
  }

  if (errorState.isError) {
    return <ErrorModal />;
  }

  return (
    <DateRangeProvider>
      <Router>
        <Switch>
          <Route path="/Builder">
            <ErrorBoundary>
              <BaseComponentProvider>
                <TemplateProvider>
                  <Builder />
                </TemplateProvider>
              </BaseComponentProvider>
            </ErrorBoundary>
          </Route>
          <Route path="/">
            <Site />
          </Route>
        </Switch>
        <PageListener />
      </Router>
      <TermsOfServiceModal
        storeName={getShop(appName)}
        onAccept={onAccept}
        disableAccept={isInternalLoginUser(appName)}
      />
    </DateRangeProvider>
  );
}
